"use client";

import { useEffect } from "react";
import { Search, X } from "@/components/icons";
import { useListingBrowseContext } from "../context/listing-browse-context";
import { cn } from "@/lib/utils";
import { useDebounce } from "@/hooks/use-debounce";
import { useSyncedState } from "@/hooks/use-synced-state";
import { IconButton } from "@/components/ui/icon-button";

interface SearchInputProps {
  className?: string;
  id?: string;
}

export function SearchInput({ className, id }: SearchInputProps) {
  const { searchQuery, setSearchQuery } = useListingBrowseContext();
  const [localSearch, setLocalSearch] = useSyncedState(searchQuery);

  const debouncedSearch = useDebounce(localSearch, 150);

  useEffect(() => {
    setSearchQuery(debouncedSearch);
  }, [debouncedSearch, setSearchQuery]);

  return (
    <div className={cn("relative", className)} data-listing-search="">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
      <input
        id={id}
        type="text"
        value={localSearch}
        onChange={(e) => setLocalSearch(e.target.value)}
        aria-label="Search listings"
        placeholder="Search listings"
        className={cn(
          "w-full h-9 pl-9 pr-8",
          "text-sm bg-card border border-border rounded-sm",
          "max-md:h-11 max-md:rounded-lg max-md:border-0 max-md:bg-surface-2 max-md:text-base",
          "text-foreground placeholder:text-muted-foreground",
          "focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/20 focus-visible:ring-[2px]",
        )}
      />
      {localSearch && (
        <IconButton
          size="inline"
          aria-label="Clear search"
          onClick={() => {
            setLocalSearch("");
            setSearchQuery("");
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
        >
          <X />
        </IconButton>
      )}
    </div>
  );
}
